import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import {
  Badge,
  Box,
  Button,
  HStack,
  Spacer,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import React, { FunctionComponent, useState } from "react";
import { QuizHeader } from "../../components/navigation/FormNavigation";
import { scrollToTop } from "../../utils/functions";
import { useTimerHook } from "./hooks";
import { QuestionPassage } from "./Passage";
import QuestionStyle from "./Question";
import { IQuizStore } from "./quizStore";
import SubmitConfirmation from "./SubmitConfirmation";
import Timer from "./Timer";

export const QuestionNavigation: FunctionComponent<{
  onNextClick: () => void;
  isLastQuestion: boolean;
  displayPreviousButton: boolean;
  onPreviousClick: () => void;
  isSelected: boolean;
  loading: boolean;
}> = ({
  onPreviousClick,
  isLastQuestion,
  onNextClick,
  displayPreviousButton,
  isSelected,
  loading,
}) => {
  return (
    <HStack
      zIndex="sticky"
      bg="white"
      bottom={0}
      left={0}
      right={0}
      py={4}
      px={{ base: 0, md: 6 }}
      width="full"
      borderTopWidth={{ base: "none", md: "2xs" }}
      borderColor="neutral.500"
      flexDirection="row-reverse"
      justifyContent="space-between"
      position={{ base: "static", md: "fixed" }}
    >
      <Button
        colorScheme="blue"
        size="lg"
        minW={12}
        alignSelf="end"
        onClick={onNextClick}
        rightIcon={<ChevronRightIcon />}
        isLoading={loading}
        loadingText={"Fetching question"}
        variant={isSelected ? "solid" : "ghost"}
      >
        {isLastQuestion ? "Submit test" : "Next"}
      </Button>
      <Spacer />
      {displayPreviousButton && (
        <Button
          size="lg"
          variant="ghost"
          alignSelf="start"
          colorScheme="brown"
          leftIcon={<ChevronLeftIcon />}
          onClick={onPreviousClick}
        >
          Prev
        </Button>
      )}
    </HStack>
  );
};

export const QuestionPage: FunctionComponent<{
  question: any;
  store: IQuizStore;
  questionNo: number;
  displayAnswer?: boolean;
  lastQuestion: number;
  nextQuestion: (...args: any[]) => any;
  previousQuestion: (...args: any[]) => void;
  selectAnswer: (...args: any[]) => void;
  quizDescription: string;
  currentAnswer: any;
  options_display: any;
  onQuizCompleted: any;
  displayQuestionNo?: boolean;
  loading: boolean;
  isCorrect?: (value: any) => boolean;
  displayPreviousButton: boolean;
  onFlagQuestion?: () => void;
}> = observer(
  ({
    question,
    questionNo,
    store,
    displayAnswer,
    quizDescription,
    displayQuestionNo,
    lastQuestion,
    nextQuestion,
    previousQuestion,
    selectAnswer,
    currentAnswer,
    options_display,
    onQuizCompleted,
    loading,
    onFlagQuestion,
    isCorrect,
    displayPreviousButton,
    ...rest
  }) => {
    const toast = useToast();
    const [canProceed, setCanProceed] = useState(currentAnswer);
    const handleAnswerClick = (value: any) => {
      setCanProceed(true);
      selectAnswer && selectAnswer(value);
    };

    const handleNextClick = () => {
      const toScroll = nextQuestion();
      if (!toScroll) {
        scrollToTop();
      }
    };

    const handlePreviousClick = () => {
      previousQuestion();
      scrollToTop();
    };

    const q = question;
    const buttonOnclick =
      questionNo === lastQuestion ? onQuizCompleted : handleNextClick;

    const onFlagClick = () => {
      if (onFlagQuestion) {
        onFlagQuestion();
      }
      toast({
        title: "Flagged for review",
        description: "Our team will review this question",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    };

    return (
      <Box className="QuestionContainer" {...rest}>
        <Box mb={{ base: 5, md: 24 }} mt={3}>
          {!displayQuestionNo && q.title ? (
            <Text
              mr={2}
              fontWeight="bold"
              fontSize={{ base: "lg", md: "xl" }}
              className="Question Title"
            >
              {q.title}
            </Text>
          ) : null}
          {q.comprehension && (
            <QuestionPassage
              badge={
                q.comprehension.kind !== "image" &&
                !q.title && (
                  <Badge mb={3} colorScheme="green">
                    PASSAGE
                  </Badge>
                )
              }
              passage={q.comprehension.passage}
            />
          )}

          <QuestionStyle
            is_horizontal={q.options_display === "horizontal"}
            question_type={q.question_type}
            questionNo={questionNo}
            onFlagClick={onFlagQuestion ? onFlagClick : undefined}
            onAnswerClick={handleAnswerClick}
            isSelected={currentAnswer}
            lastQuestion={lastQuestion}
            displayQuestionNo={displayQuestionNo}
            answers={q.answers}
            question={q.content}
            displayAnswer={displayAnswer}
            image={q.figure}
            questionTitle={q.title}
            isCorrect={isCorrect}
            direction="column"
          />
        </Box>
        <QuestionNavigation
          isSelected={canProceed}
          onNextClick={buttonOnclick}
          loading={loading}
          isLastQuestion={questionNo === lastQuestion}
          onPreviousClick={handlePreviousClick}
          displayPreviousButton={displayPreviousButton}
        />
      </Box>
    );
  },
);

const QuizPage: FunctionComponent<{
  index?: number;
  store: IQuizStore;
  subject?: string;
  options_display?: any;
  completed: boolean;
  onQuizSubmit: () => Promise<any>;
}> = observer(
  ({ index = 0, store, completed, subject, options_display, onQuizSubmit }) => {
    const { isOpen, onClose, onOpen } = useDisclosure();
    const [loading, setLoading] = React.useState(false);
    const { duration, timeElapsed } = useTimerHook({ quiz: store.quiz });
    const [submitted, showSubmit] = React.useState(false);

    React.useEffect(() => {
      if (timeElapsed) {
        onOpen();
      }
    }, [duration]);

    async function onQuizSubmission() {
      try {
        setLoading(true);
        await onQuizSubmit();
        setLoading(false);
        showSubmit(false);
        onClose();
      } catch (error) {
        throw error;
      }
    }

    return (
      <Box>
        <QuizHeader subject={subject}>
          <Box>
            {submitted && (
              <Button
                variant="link"
                colorScheme="blue"
                onClick={() => {
                  onOpen();
                  // onQuizSubmission();
                }}
              >
                Re-submit
              </Button>
            )}
            {!completed && <Timer duration={duration} />}
          </Box>
        </QuizHeader>
        <QuestionPage
          store={store}
          key={store.currentQ}
          question={store.question}
          questionNo={store.currentQ}
          quizDescription={store.getQuizDescription}
          lastQuestion={store.lastQuestion}
          nextQuestion={store.nextQuestion}
          previousQuestion={store.previousQuestion}
          currentAnswer={store.currentAnswer}
          selectAnswer={store.setAnswers}
          options_display={options_display}
          onQuizCompleted={onOpen}
          loading={store.loading}
          onFlagQuestion={() => {
            store.flagQuestion(store.question);
          }}
          displayPreviousButton={store.currentQ !== 1}
        />

        <SubmitConfirmation
          {...{
            isOpen,
            onClose,
            setCurrentQ: store.setCurrentQ,
            setShowSubmit: showSubmit,
          }}
          remainingQs={store.unansweredQs}
          questions={store.questions}
          onQuizSubmission={onQuizSubmission}
          loading={loading}
          timeElapsed={timeElapsed}
          // styles={styles.submitConfirmation}
        />
      </Box>
    );
  },
);

export default QuizPage;
